import type { ModelName } from '@pigello/pigello-matrix';
import { DateTime } from 'luxon';
import type { IdentifierMapping } from '..';

export function getContractMapping(
  modelName: ModelName
): IdentifierMapping[string][string] {
  return {
    warning_contracts: {
      modelName,
      filters: {
        is_draft: {
          noop: false,
        },
        status: {
          __in: '7',
        },
      },
    },
    cancelled_contracts: {
      modelName,
      filters: {
        is_draft: {
          noop: false,
        },
        status: {
          __in: '3,4,5,6',
        },
      },
    },
    not_signed_contracts: {
      modelName,
      filters: {
        is_draft: {
          noop: false,
        },
        status: {
          __in: '10',
        },
      },
    },
    upcoming_contracts: {
      modelName,
      filters: {
        is_draft: {
          noop: false,
        },
        status: {
          __in: '9',
        },
        start_date: {
          __gt: DateTime.now().toISODate(),
        },
      },
    },
    currently_signed_contracts: {
      modelName: 'signabledocument',
      content_type: `contracts.${modelName}`,
      filters: {
        signing_initiated: { noop: true },
        signing_aborted_time: { __isnull: true },
        signed_time: { __isnull: true },
        rejected_time: {
          __isnull: true,
        },
        attached_to: { __isnull: true },
        is_template: {
          noop: false,
        },
      },
      plainUrlReturn: true,
    },
    cancelled_signed_contracts: {
      modelName: 'signabledocument',
      content_type: `contracts.${modelName}`,
      plainUrlReturn: true,
      filters: {
        signing_initiated: { noop: false },
        signed_time: { __isnull: true },
        signing_aborted_time: { __isnull: false },
      },
    },
  };
}

export function getTenantMapping(
  modelName: ModelName,
  show_in_brf_context?: boolean
): Omit<IdentifierMapping[string][string], 'timeline_attributes'> {
  return {
    tenants_on_my_pages: {
      modelName,
      filters: {
        is_active: {
          noop: true,
        },
        ...(show_in_brf_context && {
          show_in_brf_context: {
            noop: true,
          },
        }),
      },
    },
    tenants_invited_to_my_pages: {
      modelName,
      filters: {
        is_active: {
          noop: false,
        },
        ...(show_in_brf_context && {
          show_in_brf_context: {
            noop: true,
          },
        }),
        hotp_invite_sendout_time: { __isnull: false },
      },
    },
  };
}

export function getObjectsMapping(): IdentifierMapping[string][string] {
  return {
    no_swedish_price_base: {
      modelName: 'swedishpricebasevalue',
      filters: {
        for_year: { noop: DateTime.now().year },
      },
    },
  };
}

const vacancyFilters = {
  status: {
    __in: '9,10,11,12',
  },
};

export function getVacancyMapping(): IdentifierMapping[string][string] {
  return {
    brfapartment: {
      modelName: 'brfapartment',
      filters: vacancyFilters,
    },
    apartment: {
      modelName: 'apartment',
      filters: vacancyFilters,
    },
    industrialpremises: {
      modelName: 'industrialpremises',
      filters: vacancyFilters,
    },
    outdoorsection: {
      modelName: 'outdoorsection',
      filters: vacancyFilters,
    },
    parkingspot: {
      modelName: 'parkingspot',
      filters: vacancyFilters,
    },
    commonarea: {
      modelName: 'commonarea',
      filters: vacancyFilters,
    },
  };
}

export function getInvoiceMapping(
  modelName: ModelName,
  non_rental_invoices?: boolean
): IdentifierMapping[string][string] {
  return {
    non_attested_invoices: {
      modelName,
      filters: {
        custom_invoice_filter: { noop: ['not_attested'] },
        rental_invoice: { noop: !non_rental_invoices },
      },
    },
    not_paid_invoices: {
      modelName,
      filters: {
        rental_invoice: { noop: !non_rental_invoices },
        attested_date: { __isnull: false },
        closed_time: { __isnull: true },
      },
    },
    overdue_invoices: {
      modelName,
      filters: {
        rental_invoice: { noop: !non_rental_invoices },
        attested_date: { __isnull: false },
        closed_time: { __isnull: true },
        due_date: {
          __lt: DateTime.now().toISODate(),
        },
      },
    },
    merged_failed_coinvoice: {
      modelName: 'coinvoicenotation',
      filters: {
        failed: { noop: true },
        invoice: { __isnull: false },
      },
      plainUrlReturn: true,
    },
    autogiro_failed: {
      modelName: 'autogirostatus',
      filters: {
        status_flag: { noop: 2 },
      },
      noFilterReturn: true,
    },
    invoice_sendout_failed: {
      overrideUrl: true,
    },
  };
}
